import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { createStripeClient } from "@/lib/stripe.server";
import { z } from "zod";

// Keep in sync with src/routes/billing.tsx
const MONTHLY_PAYMENT_LINK = "https://buy.stripe.com/aFafZh7DS5y89Zx7ZSgfu00";
const YEARLY_PAYMENT_LINK = "https://buy.stripe.com/eVq4gz4rG8Kk9Zxdkcgfu01";
type Locale = "fr" | "en" | "nl";
const InputSchema = z.object({ locale: z.enum(["fr", "en", "nl"]).default("fr") }).optional();
const PLAN_LABELS: Record<Locale, Record<string, string>> = {
  fr: { premium_monthly: "Mensuel", premium_yearly: "Annuel" },
  en: { premium_monthly: "Monthly", premium_yearly: "Yearly" },
  nl: { premium_monthly: "Maandelijks", premium_yearly: "Jaarlijks" },
};
const TEXT: Record<Locale, { ready: string; incomplete: string; warning: string }> = {
  fr: { ready: "Tout est prêt pour encaisser en Live.", incomplete: "Le mode Live n'est pas prêt. Terminez les étapes ci-dessous.", warning: "Le mode Live est presque prêt — quelques vérifications à confirmer." },
  en: { ready: "Everything is ready to take live payments.", incomplete: "Live mode is not ready. Complete the steps below.", warning: "Live mode is almost ready — a few checks still need confirmation." },
  nl: { ready: "Alles is klaar om live betalingen te ontvangen.", incomplete: "Live-modus is nog niet klaar. Rond de stappen hieronder af.", warning: "Live-modus is bijna klaar — enkele controles moeten nog bevestigd worden." },
};
const EXPECTED_LINKS: Array<{ url: string; lookupKey: string }> = [
  { url: MONTHLY_PAYMENT_LINK, lookupKey: "premium_monthly" },
  { url: YEARLY_PAYMENT_LINK, lookupKey: "premium_yearly" },
];

export type LiveStatusCheck = {
  id: string;
  label: string;
  status: "ok" | "warning" | "fail";
  detail: string;
};

export type LiveStatusResponse = {
  overall: "ready" | "incomplete" | "error";
  checked_at: string;
  checks: LiveStatusCheck[];
  summary: string;
};

export const getLiveStatus = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => InputSchema.parse(input) ?? { locale: "fr" as const })
  .handler(async ({ data }): Promise<LiveStatusResponse> => {
    const locale = data.locale;
    const checks: LiveStatusCheck[] = [];
    const checkedAt = new Date().toISOString();

    const hasLiveKey = !!process.env.STRIPE_LIVE_API_KEY;
    const hasLiveWebhook = !!process.env.PAYMENTS_LIVE_WEBHOOK_SECRET;

    checks.push({
      id: "live_api_key",
      label: "Clé API Live provisionnée",
      status: hasLiveKey ? "ok" : "fail",
      detail: hasLiveKey
        ? "STRIPE_LIVE_API_KEY est présente côté serveur."
        : "Aucune clé Live détectée. Terminez le claim + le formulaire Stripe + l'installation de l'app Lovable côté Live.",
    });

    checks.push({
      id: "live_webhook_secret",
      label: "Secret webhook Live configuré",
      status: hasLiveWebhook ? "ok" : "fail",
      detail: hasLiveWebhook
        ? "PAYMENTS_LIVE_WEBHOOK_SECRET est présent côté serveur."
        : "Secret manquant. Il est provisionné automatiquement par Lovable après l'installation de l'app sur le compte Live.",
    });

    if (hasLiveKey) {
      try {
        const stripe = createStripeClient("live");
        const prices = await stripe.prices.list({
          lookup_keys: ["premium_monthly", "premium_yearly"],
          active: true,
          limit: 10,
        });
        const foundKeys = new Set(
          prices.data.map((p) => p.lookup_key).filter(Boolean) as string[],
        );
        const missing = ["premium_monthly", "premium_yearly"].filter(
          (k) => !foundKeys.has(k),
        );
        checks.push({
          id: "live_prices",
          label: "Prix Premium présents en Live",
          status: missing.length === 0 ? "ok" : "warning",
          detail:
            missing.length === 0
              ? `Trouvé: ${[...foundKeys].join(", ")}.`
              : `Manquants en Live: ${missing.join(", ")}. Copiez vos produits depuis le sandbox ou recréez-les.`,
        });

        const inclusive = prices.data.filter((p) => p.tax_behavior === "inclusive");
        checks.push({
          id: "live_tax_inclusive",
          label: "tax_behavior=inclusive sur les prix Live",
          status:
            prices.data.length === 0
              ? "warning"
              : inclusive.length === prices.data.length
                ? "ok"
                : "warning",
          detail:
            prices.data.length === 0
              ? "Aucun prix à vérifier."
              : `${inclusive.length}/${prices.data.length} prix Live en inclusive.`,
        });

        // Verify each hardcoded Payment Link exists in Live, is active,
        // and points to the matching premium lookup_key.
        try {
          // Paginate up to 300 to be safe; most accounts have <20.
          const allLinks: Array<any> = [];
          let starting_after: string | undefined;
          for (let i = 0; i < 3; i++) {
            const page = await stripe.paymentLinks.list({
              limit: 100,
              ...(starting_after ? { starting_after } : {}),
            });
            allLinks.push(...page.data);
            if (!page.has_more) break;
            starting_after = page.data[page.data.length - 1]?.id;
          }

          for (const expected of EXPECTED_LINKS) {
            const planLabel = PLAN_LABELS[locale][expected.lookupKey] ?? expected.lookupKey;
            const match = allLinks.find((l) => l.url === expected.url);
            if (!match) {
              checks.push({
                id: `live_payment_link_${expected.lookupKey}`,
                label: `Payment Link ${planLabel} en Live`,
                status: "fail",
                detail: `URL introuvable sur le compte Live (${expected.url}). Recréez le Payment Link en mode Live et mettez à jour l'URL dans src/routes/billing.tsx.`,
              });
              continue;
            }
            if (!match.active) {
              checks.push({
                id: `live_payment_link_${expected.lookupKey}`,
                label: `Payment Link ${planLabel} en Live`,
                status: "fail",
                detail: `Trouvé mais désactivé. Réactivez-le dans Stripe → Payment Links.`,
              });
              continue;
            }
            // Hydrate line items to check the lookup_key
            const items = await stripe.paymentLinks.listLineItems(match.id, { limit: 5 });
            const keys = items.data
              .map((it: any) => it.price?.lookup_key)
              .filter(Boolean) as string[];
            const matches = keys.includes(expected.lookupKey);
            checks.push({
              id: `live_payment_link_${expected.lookupKey}`,
              label: `Payment Link ${planLabel} en Live`,
              status: matches ? "ok" : "warning",
              detail: matches
                ? `Actif en Live, lié à ${expected.lookupKey}.`
                : `Actif en Live mais lookup_key inattendu: ${keys.join(", ") || "aucun"}. Attendu: ${expected.lookupKey}.`,
            });
          }
        } catch (linkError) {
          checks.push({
            id: "live_payment_links",
            label: "Payment Links Live",
            status: "fail",
            detail:
              linkError instanceof Error
                ? `Impossible de lister les Payment Links Live: ${linkError.message}`
                : "Impossible de lister les Payment Links Live.",
          });
        }
      } catch (error) {
        checks.push({
          id: "live_api_call",
          label: "Appel API Stripe Live",
          status: "fail",
          detail:
            error instanceof Error
              ? `Échec: ${error.message}`
              : "Échec inconnu lors de l'appel Stripe Live.",
        });
      }
    }

    const hasFail = checks.some((c) => c.status === "fail");
    const hasWarn = checks.some((c) => c.status === "warning");
    const overall: LiveStatusResponse["overall"] = hasFail
      ? "incomplete"
      : hasWarn
        ? "incomplete"
        : "ready";

    const summary = hasFail
      ? TEXT[locale].incomplete
      : hasWarn
        ? TEXT[locale].warning
        : TEXT[locale].ready;

    return { overall, checked_at: checkedAt, checks, summary };
  });